import { useEffect } from 'react';

interface Options<T> {
  intervalMs: number;
  until?: (result: T) => boolean;
  onFinish?: (result: T) => void;
  enabled?: boolean;
}

export function usePolling<T>(
  fn: () => Promise<T>,
  { intervalMs, until, onFinish, enabled = true }: Options<T>,
): void {
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const res = await fn();
        if (cancelled) return;
        if (until?.(res)) {
          onFinish?.(res);
          return;
        }
        timer = setTimeout(() => { void poll(); }, intervalMs);
      } catch {
        timer = setTimeout(() => { void poll(); }, intervalMs);
      }
    };

    void poll();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [fn, intervalMs, until, onFinish, enabled]);
}
